import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LanguageLevel } from '../types/database'
import { DataLayer } from '../lib/dataLayer'

interface CardData {
  front: string
  back: string
  level?: LanguageLevel
}

interface BatchAddProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export function BatchAdd({ onSuccess, onCancel }: BatchAddProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  const [language, setLanguage] = useState('es')
  const [defaultLevel, setDefaultLevel] = useState<LanguageLevel>('B1')
  const [tagInput, setTagInput] = useState('')
  const [jsonInput, setJsonInput] = useState('')

  const sampleJson = `[
  {
    "front": "Hello",
    "back": "Hola",
    "level": "A1"
  },
  {
    "front": "Thank you",
    "back": "Gracias"
  },
  {
    "front": "Good morning",
    "back": "Buenos días"
  }
]`

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setError(null)
    setSuccess(null)
    setLoading(true)

    try {
      // Parse JSON input
      let cards: CardData[]
      try {
        cards = JSON.parse(jsonInput)
      } catch (err) {
        throw new Error('Invalid JSON format. Please check your input.')
      }

      // Validate cards
      if (!Array.isArray(cards)) {
        throw new Error('JSON must be an array of card objects.')
      }

      for (let i = 0; i < cards.length; i++) {
        if (!cards[i].front || !cards[i].back) {
          throw new Error(`Card ${i + 1} is missing front or back field.`)
        }
      }

      // Parse tags
      const tags = tagInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)

      // Insert all cards using DataLayer
      for (const card of cards) {
        await DataLayer.createCard({
          front: card.front,
          back: card.back,
          language,
          level: card.level || defaultLevel,
          tags,
          order: null,
          source_id: null,
          exercise_id: null,
          audio_url: null
        }, user.id)
      }

      setSuccess(`Successfully added ${cards.length} cards!`)
      setJsonInput('')
      
      // Call onSuccess after a short delay to show the success message
      setTimeout(() => {
        if (onSuccess) onSuccess()
      }, 2000)

    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">Batch Add Cards</h2>
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Back to Cards
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                  Tags for all cards (comma-separated)
                </label>
                <input
                  id="tags"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="vocabulary, chapter-1, verbs..."
                />
              </div>

              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                  Language for all cards
                </label>
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="it">Italian</option>
                  <option value="pt">Portuguese</option>
                  <option value="ru">Russian</option>
                  <option value="ja">Japanese</option>
                  <option value="ko">Korean</option>
                  <option value="zh">Chinese</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="level" className="block text-sm font-medium text-gray-700">
                Default Level for cards without level
              </label>
              <select
                id="level"
                value={defaultLevel}
                onChange={(e) => setDefaultLevel(e.target.value as LanguageLevel)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="A1">A1 - Beginner</option>
                <option value="A2">A2 - Elementary</option>
                <option value="B1">B1 - Intermediate</option>
                <option value="B2">B2 - Upper Intermediate</option>
                <option value="C1">C1 - Advanced</option>
                <option value="C2">C2 - Proficient</option>
              </select>
            </div>

            <div>
              <label htmlFor="json" className="block text-sm font-medium text-gray-700">
                Cards JSON (array of objects with "front", "back", and optional "level" fields)
              </label>
              <textarea
                id="json"
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                required
                rows={15}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono text-xs"
                placeholder={sampleJson}
              />
              <p className="mt-2 text-sm text-gray-500">
                Example format:
                <pre className="mt-1 text-xs bg-gray-50 p-2 rounded overflow-x-auto">
{`[
  {"front": "Hello", "back": "Hola", "level": "A1"},
  {"front": "Thank you", "back": "Gracias"},
  {"front": "How are you?", "back": "¿Cómo estás?", "level": "A2"}
]`}
                </pre>
              </p>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {success && (
              <div className="rounded-md bg-green-50 p-4">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Adding Cards...' : 'Add Cards'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}